import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { kostenApi } from '../api';

const TABS = ['Übersicht','Prognose','Einsparungen'];

export default function KostenPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [uebersicht, setUebersicht] = useState(null);
  const [prognose, setPrognose] = useState([]);
  const [einsparungen, setEinsparungen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setErr(null);
      try {
        const [u, p, e] = await Promise.all([
          kostenApi.uebersicht(),
          kostenApi.prognose(12),
          kostenApi.einsparungen(),
        ]);
        setUebersicht(u); setPrognose(p); setEinsparungen(e);
      } catch (e) { setErr(e.response?.data?.error || e.message); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6366f1' }}>⏳ Lade…</div>;
  if (err) return <div style={{ padding: 20, color: '#f87171' }}>{err}</div>;

  return (
    <div style={{ padding: 20, color: '#f1f5f9', maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>💳 Kostenanalyse</h1>
        <button onClick={() => navigate('/vertraege')} className="btn-secondary">Zu Verträgen</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #334155' }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            background: tab === i ? '#818cf8' : 'transparent', color: tab === i ? '#fff' : '#94a3b8',
            border: 'none', padding: '8px 14px', cursor: 'pointer', fontSize: 13,
            borderRadius: '6px 6px 0 0', fontWeight: tab === i ? 600 : 400
          }}>{t}</button>
        ))}
      </div>

      {/* Übersicht */}
      {tab === 0 && uebersicht && (
        <div>
          {/* Kennzahlen */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 14, marginBottom: 24 }}>
            <KpiCard label="Monatskosten" value={`${(uebersicht.monatskosten || 0).toLocaleString('de-DE', {minimumFractionDigits:2})} €`} color="#6366f1" />
            <KpiCard label="Jahreskosten" value={`${(uebersicht.jahreskosten || 0).toLocaleString('de-DE', {minimumFractionDigits:2})} €`} color="#3b82f6" />
            <KpiCard label="Einmalige Kosten" value={`${(uebersicht.einmalig || 0).toLocaleString('de-DE', {minimumFractionDigits:2})} €`} color="#f59e0b" />
            <KpiCard label="Aktive Positionen" value={uebersicht.positionen || 0} color="#10b981" />
          </div>

          {/* Nach Kategorie */}
          {(uebersicht.nach_kategorie || []).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Nach Kategorie</h3>
                {uebersicht.nach_kategorie.map(k => (
                  <div key={k.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #0f172a', fontSize: 13 }}>
                    <span>{k.name}</span>
                    <span style={{ color: '#6366f1', fontWeight: 500 }}>{k.monatskosten.toLocaleString('de-DE', {minimumFractionDigits:2})} €/Monat</span>
                  </div>
                ))}
              </div>
              <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Nach Anbieter</h3>
                {uebersicht.nach_anbieter.map(a => (
                  <div key={a.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #0f172a', fontSize: 13 }}>
                    <span>{a.name}</span>
                    <span style={{ color: '#3b82f6', fontWeight: 500 }}>{a.monatskosten.toLocaleString('de-DE', {minimumFractionDigits:2})} €/Monat</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Prognose */}
      {tab === 1 && (
        <div>
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>Monatsprognose (12 Monate)</h3>
            <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 16px' }}>
              Basiert auf aktuell aktiven Kostenpositionen. Änderungen und neue Verträge nicht berücksichtigt.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #334155' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748b', fontWeight: 500 }}>Monat</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#64748b', fontWeight: 500 }}>Monatskosten</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#64748b', fontWeight: 500 }}>Datenbasis</th>
                  </tr>
                </thead>
                <tbody>
                  {prognose.map(p => (
                    <tr key={p.monat} style={{ borderBottom: '1px solid #0f172a' }}>
                      <td style={{ padding: '8px 12px' }}>{p.monat}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6366f1', fontWeight: 500 }}>
                        {p.monatskosten.toLocaleString('de-DE', {minimumFractionDigits:2})} €
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b' }}>
                        {p.sicher ? 'Aktive Positionen' : 'Schätzung'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ color: '#475569', fontSize: 12, marginTop: 12 }}>
              Hinweis: Die Prognose berechnet sich aus aktiven Kostenpositionen. Sie stellt keine Beratung dar und berücksichtigt keine Marktdaten.
            </p>
          </div>
        </div>
      )}

      {/* Einsparungen */}
      {tab === 2 && einsparungen && (
        <div>
          {(einsparungen.hinweise || []).length === 0 ? (
            <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>
              <div style={{ fontSize: 36 }}>✅</div>
              <div style={{ marginTop: 10 }}>Keine Einsparpotenziale erkannt.</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {einsparungen.hinweise.map((h, i) => (
                <div key={i} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
                  <div style={{ fontWeight: 500, marginBottom: 8 }}>💡 {h.grund}</div>
                  {h.kategorie && <div style={{ color: '#94a3b8', fontSize: 13 }}>Kategorie: {h.kategorie}</div>}
                  {h.anzahl_vertraege && <div style={{ color: '#94a3b8', fontSize: 13 }}>{h.anzahl_vertraege} Verträge</div>}
                  <div style={{ color: '#f59e0b', fontSize: 13, marginTop: 8 }}>{h.empfehlung}</div>
                  {h.hinweis && <div style={{ color: '#64748b', fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>⚠ {h.hinweis}</div>}
                </div>
              ))}
            </div>
          )}
          <p style={{ color: '#475569', fontSize: 12, marginTop: 16 }}>
            Basisdatum: {einsparungen.basisdatum} – Alle Einsparpotenziale sind regelbasierte Hinweise auf Basis eigener Daten, keine Marktpreisvergleiche.
          </p>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, color }) {
  return (
    <div style={{ background: '#1e293b', border: `1px solid ${color}33`, borderRadius: 10, padding: 16 }}>
      <div style={{ color: '#64748b', fontSize: 12, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ color, fontSize: 22, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

